import { Box, Stack, Tooltip } from '@mui/material';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { DeckDetails, DeckImg, DeckName, ErrorContainer, QueryResults } from './styles';
import { useDecksForDeckListQuery, Maybe, DeckAreaSummary } from '@/__generated__/graphql';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { theme } from '@/theme/theme';
import { useDragToScroll } from '@/hooks';
import { useGetStructureId } from '@/hooks/useGetStructureId';

type Props = {
  handleSelection: (item: Deck) => void;
  handleSetLoading: (loading: boolean) => void;
  filteredAssemblyDecks?: Maybe<DeckAreaSummary[]> | undefined;
  previousDeck?: Deck | null;
  selectedDeck?: Deck | null;
  selectDefaultDeck?: boolean;
  loading?: boolean;
};

export interface Deck {
  id: string;
  resourcePath: string;
  name: string;
  heightRange: number[];
  heightStep?: Maybe<number> | undefined;
}

export const DeckList = ({
  handleSelection,
  handleSetLoading,
  filteredAssemblyDecks,
  previousDeck,
  selectedDeck,
  loading = false,
  selectDefaultDeck = false,
}: Props) => {
  const [decks, setDecks] = useState<Array<Deck> | null>();
  const [imageError, setImageError] = useState<{ [key: string]: boolean | undefined }>({});
  const { containerReference, handleMouseDown } = useDragToScroll();
  const structureId = useGetStructureId();
  const { loading: allDecksLoading, data } = useDecksForDeckListQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    handleSetLoading(allDecksLoading);
    const allDecks = data?.allDecks;
    if (!allDecksLoading && allDecks) {
      setDecks(allDecks);
      const currentDeck = allDecks.find((deck) => deck.id === selectedDeck?.id) || allDecks[0];
      if (currentDeck && selectDefaultDeck) {
        handleSelection(currentDeck);
      }
    }
  }, [
    data,
    loading,
    setDecks,
    handleSelection,
    handleSetLoading,
    selectedDeck?.id,
    selectDefaultDeck,
    allDecksLoading,
  ]);

  const checkSelectedDeck = useCallback(
    (deck: Deck) => {
      return selectedDeck?.id === deck.id || previousDeck?.id === deck.id;
    },
    [selectedDeck, previousDeck]
  );

  const fallbackImageHandler = (
    event: React.SyntheticEvent<HTMLImageElement, Event>,
    id: string
  ) => {
    console.error('DeckList.tsx -> fallbackImageHandler() ->', event);
    const target = event.target as HTMLImageElement;
    target.src = '/assets/fallback_deckplan_overlay.png';
    setImageError((previousState) => {
      return { ...previousState, [id]: true };
    });
  };

  const filteredDecks = useMemo(() => {
    let isDisabled = false;
    let queryPercentage = 0;
    const deckIds = filteredAssemblyDecks?.map((deck) => deck?.deckId);
    const totalQueryArea = filteredAssemblyDecks?.reduce(
      (accumulator, currentValue) => accumulator + currentValue.totalArea,
      0
    );

    const handleDeckClick = (deck: Deck) => {
      if (selectDefaultDeck || deckIds?.includes(deck.id)) {
        handleSelection(deck);
      }
    };

    return decks?.map((item) => {
      if (deckIds && filteredAssemblyDecks) {
        isDisabled = !deckIds?.includes(item.id);
        const area = filteredAssemblyDecks.find((deck) => deck?.deckId === item.id)?.totalArea;
        queryPercentage = totalQueryArea && area ? (area / totalQueryArea) * 100 : 0;
      }

      return (
        <Box
          sx={{
            position: 'relative',
            height: '86px',
            overflowY: 'hidden',
            '@media only screen and (max-width: 1728px)': {
              height: '72px',
            },
          }}
          key={item.id}
          onClick={() => handleDeckClick(item)}
        >
          <DeckImg
            src={getCloudfrontUrl(item.resourcePath)}
            crossOrigin="use-credentials"
            alt="deck image"
            onError={(event) => fallbackImageHandler(event, item.id)}
            style={{
              border: checkSelectedDeck(item)
                ? `3px solid ${theme.palette.common.darkBlue}`
                : '1px solid #bbb',
              opacity: selectedDeck?.id === item.id ? 1 : 0 || isDisabled ? 0.5 : 1,
            }}
          />
          {imageError[item?.id] && (
            <ErrorContainer src="/assets/fallback_deckplan.svg" alt="group icon" />
          )}
          {!imageError[item?.id] && (
            <DeckDetails>
              <Tooltip title={item.name} placement="bottom">
                <DeckName>{item.name}</DeckName>
              </Tooltip>
              <QueryResults>{queryPercentage.toFixed(0)}%</QueryResults>
            </DeckDetails>
          )}
        </Box>
      );
    });
  }, [
    filteredAssemblyDecks,
    decks,
    selectDefaultDeck,
    checkSelectedDeck,
    selectedDeck?.id,
    handleSelection,
    imageError,
  ]);

  return (
    <>
      {!loading && (
        <Box
          ref={containerReference}
          sx={{
            overflow: 'scroll',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Stack
            direction="row"
            spacing={2}
            sx={{ overflow: 'auto', width: 'fit-content', cursor: 'pointer' }}
            onMouseDown={handleMouseDown}
          >
            {decks && filteredDecks}
          </Stack>
        </Box>
      )}
    </>
  );
};
